import { __assign, __rest } from "tslib";
import { useMemo, useEffect, useCallback } from 'react';
import throttle from 'lodash/throttle';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { selectors as streamSelectors, actions as streamActions, } from '../../Stream';
import { createGetDeviationsByIds } from '../../redux/deviations/selectors';
import { createGetDenormalizedDataEntitiesByIds } from '../../redux/entities/selectors';
import { galleryFolderSchema, collectionFolderSchema, } from '../../redux/normalizr/schemas/folder';
import { createGetUsersByIds } from '../../redux/users/selectors';
import { initializeIfNeeded } from '../../Stream/base/actions';
export function useStream(streamId, throttleTimeout, initConfig) {
    if (throttleTimeout === void 0) { throttleTimeout = 500; }
    var dispatch = useDispatch();
    useEffect(function () {
        if (!initConfig) {
            return;
        }
        dispatch(initializeIfNeeded(streamId, initConfig));
    }, [streamId, initConfig, dispatch]);
    var stream = useSelector(function (state) { return ({
        items: streamSelectors.getItems(state, streamId),
        total: streamSelectors.getTotal(state, streamId),
        hasMore: streamSelectors.hasMore(state, streamId),
        hasLess: streamSelectors.getHasLess(state, streamId),
        isFetching: streamSelectors.isFetching(state, streamId),
        isInitialized: streamSelectors.isInitialized(state, streamId),
        errorMessage: streamSelectors.getErrorMsg(state, streamId),
    }); }, shallowEqual);
    var fetchNext = useMemo(function () {
        return throttle(function () {
            dispatch(streamActions.fetchNext(streamId));
        }, throttleTimeout);
    }, [dispatch, streamId, throttleTimeout]);
    var fetchPrevious = useMemo(function () {
        return throttle(function () {
            dispatch(streamActions.fetchPrevious(streamId));
        }, throttleTimeout);
    }, [dispatch, streamId, throttleTimeout]);
    var fetchAll = useMemo(function () {
        return throttle(function () {
            dispatch(streamActions.fetchAll(streamId));
        }, throttleTimeout);
    }, [dispatch, streamId, throttleTimeout]);
    var cancelFetch = useCallback(function () { return dispatch(streamActions.cancelFetchForStream(streamId)); }, [dispatch, streamId]);
    useEffect(function () {
        return function () {
            fetchNext.cancel();
            fetchPrevious.cancel();
            fetchAll.cancel();
        };
    }, [fetchNext, fetchPrevious, fetchAll]);
    return __assign(__assign({}, stream), { fetchNext: fetchNext, fetchPrevious: fetchPrevious, fetchAll: fetchAll, cancelFetch: cancelFetch });
}
export function useNormalizedDeviationStream(streamId, throttleTimeout, initConfig) {
    if (throttleTimeout === void 0) { throttleTimeout = 500; }
    var _a = useStream(streamId, throttleTimeout, initConfig), items = _a.items, restStream = __rest(_a, ["items"]);
    var getDeviationsByIds = useMemo(function () { return createGetDeviationsByIds(); }, []);
    var deviations = useSelector(function (state) { return getDeviationsByIds(state, items); });
    return __assign(__assign({}, restStream), { items: deviations });
}
export function useNormalizedUserStream(streamId, throttleTimeout, initConfig) {
    if (throttleTimeout === void 0) { throttleTimeout = 500; }
    var _a = useStream(streamId, throttleTimeout, initConfig), items = _a.items, restStream = __rest(_a, ["items"]);
    var getUsersByIds = useMemo(function () { return createGetUsersByIds(); }, []);
    var users = useSelector(function (state) { return getUsersByIds(state, items); });
    return __assign(__assign({}, restStream), { items: users });
}
export function useNormalizedFolderStream(streamId, gallectionType) {
    var _a = useStream(streamId), items = _a.items, restStream = __rest(_a, ["items"]);
    var getDenormalizedDataEntitiesByIds = useMemo(function () { return createGetDenormalizedDataEntitiesByIds(); }, []);
    var folders = useSelector(function (state) {
        return getDenormalizedDataEntitiesByIds(state, gallectionType === 'gallery'
            ? galleryFolderSchema
            : collectionFolderSchema, items);
    });
    return __assign(__assign({}, restStream), { items: folders });
}
//# sourceMappingURL=useStream.js.map